import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { motion } from 'framer-motion';

const HOLD_DURATION = 2200; // faster hold

export function LockInScreen() {
  const navigate = useNavigate();
  const { nextStep } = useStore();
  const [progress, setProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const frameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const holdingRef = useRef(false);

  const startHold = useCallback(() => {
    if (isComplete) return;

    setIsHolding(true);
    holdingRef.current = true;
    startTimeRef.current = performance.now();

    const tick = (now: number) => {
      if (!holdingRef.current) return;
      const elapsed = now - startTimeRef.current;
      const newProgress = Math.min(elapsed / HOLD_DURATION, 1);
      setProgress(newProgress);

      if (newProgress >= 1) {
        holdingRef.current = false;
        setIsComplete(true);
        setTimeout(() => {
          nextStep();
          navigate('/onboarding/hype');
        }, 500);
        return;
      }

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
  }, [isComplete, nextStep, navigate]);

  const stopHold = useCallback(() => {
    if (isComplete) return;

    setIsHolding(false);
    holdingRef.current = false;
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
    setProgress(0);
  }, [isComplete]);

  // SVG circle parameters
  const size = 200;
  const strokeWidth = 14;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <motion.div
      className="min-h-screen flex flex-col items-center justify-center px-6 onboarding-screen"
      initial={{ backgroundColor: '#000000' }}
      animate={{
        backgroundColor: isComplete ? 'rgba(212, 175, 55, 0.2)' : '#000000',
      }}
      transition={{ duration: 0.3 }}
    >
      <motion.h1
        className="text-3xl font-bold mb-4 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        Time to lock in.
      </motion.h1>

      <motion.p
        className="text-secondary text-center mb-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        Commit to becoming your best version.
      </motion.p>

      {/* Fingerprint button with progress ring */}
      <div
        className="relative cursor-pointer select-none"
        onMouseDown={startHold}
        onMouseUp={stopHold}
        onMouseLeave={stopHold}
        onTouchStart={startHold}
        onTouchEnd={stopHold}
      >
        {/* Red overlay fill */}
        <div
          className="absolute inset-4 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255, 80, 80, 0.35) 0%, rgba(255, 0, 0, 0.08) 70%, rgba(255, 0, 0, 0) 100%)',
            opacity: 0.2 + progress * 0.5,
          }}
        />
        {/* Progress ring */}
        <svg
          width={size}
          height={size}
          className="transform -rotate-90"
        >
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--color-border)"
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
          />
        </svg>

        {/* Fingerprint icon */}
        <div
          className="absolute inset-0 flex items-center justify-center"
        >
          <motion.div
            animate={{
              scale: isHolding ? 0.95 : 1,
              opacity: isComplete ? 0.5 : 1,
            }}
            transition={{ duration: 0.1 }}
          >
            <svg
              className="w-24 h-24"
              fill="none"
              stroke={isComplete ? 'var(--color-accent)' : 'currentColor'}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"
              />
            </svg>
          </motion.div>
        </div>
      </div>

      <motion.p
        className="text-sm text-muted mt-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: isHolding ? 0 : 1 }}
        transition={{ duration: 0.2 }}
      >
        Press and hold
      </motion.p>

      {/* Completion checkmark */}
      {isComplete && (
        <motion.div
          className="absolute"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <svg className="w-16 h-16 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </motion.div>
      )}
    </motion.div>
  );
}
